/**
 * How close a press must be to grab an annotation's handle (to edit it) or its line (to move it).
 *
 * The pinned cornerstone-tools beta grabs a handle within 6 px and moves a whole line within 25 px,
 * so a press slightly off a handle dragged the line instead of editing the point. Handles are checked
 * first, so a handle zone at least as large as the move zone makes editing win near a handle; the move
 * zone is also tighter, so a press near (not on) a line no longer drags it away.
 *
 * Configurable per deployment in viewer_config.js (IMEXHS v8 uses the same three keys and defaults):
 * `annot_handle_proximity`, `annot_move_proximity`, `annot_touch_proximity` (CSS px).
 * A finger is less precise than a cursor: touch presses use the touch distance for handles and half
 * of it for moving.
 */

export interface ProximityConfig {
  handle: number;
  move: number;
  touch: number;
}

export const DEFAULT_PROXIMITY: ProximityConfig = { handle: 12, move: 8, touch: 24 };
const MIN_PX = 2;
const MAX_PX = 64;

export type PointerKind = 'mouse' | 'touch';

function px(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= MIN_PX && n <= MAX_PX ? n : fallback;
}

/** The three distances from a config object (bad or missing values fall back to the defaults). */
export function proximityFrom(config: Record<string, unknown> | undefined): ProximityConfig {
  const move = px(config?.annot_move_proximity, DEFAULT_PROXIMITY.move);
  return {
    move,
    // Never smaller than the move zone, or a press near a handle would move the line.
    handle: Math.max(move, px(config?.annot_handle_proximity, DEFAULT_PROXIMITY.handle)),
    touch: px(config?.annot_touch_proximity, DEFAULT_PROXIMITY.touch),
  };
}

export function handleDistance(config: ProximityConfig, kind: PointerKind): number {
  return kind === 'touch' ? Math.max(config.touch, config.handle) : config.handle;
}

export function moveDistance(config: ProximityConfig, kind: PointerKind): number {
  return kind === 'touch' ? Math.max(config.move, Math.round(config.touch / 2)) : config.move;
}

// --- Runtime state (the tools ask; the input listener below keeps it current) ------------------

let config: ProximityConfig = DEFAULT_PROXIMITY;
let lastKind: PointerKind = 'mouse';

export function currentMoveDistance(): number {
  return moveDistance(config, lastKind);
}

interface ToolsStore {
  state: { clickProximity: number };
}

/**
 * Reads the config and keeps cornerstone-tools' handle distance matched to the input in use: a
 * capturing pointerdown runs before the tools' own mousedown / touchstart handling of the same press.
 */
export function installProximity(runtimeConfig: Record<string, unknown> | undefined, store: ToolsStore | undefined): void {
  config = proximityFrom(runtimeConfig);
  const apply = (kind: PointerKind) => {
    lastKind = kind;
    if (store?.state) store.state.clickProximity = handleDistance(config, kind);
  };
  apply('mouse');
  document.addEventListener('pointerdown', (event) => apply(event.pointerType === 'mouse' ? 'mouse' : 'touch'), { capture: true });
  document.addEventListener('touchstart', () => apply('touch'), { capture: true, passive: true });
}
